class ProductMobilePurchaseRail extends HTMLElement {
  mediaQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    this.productDetails = this.closest('.product-details');
    this.sourceContainer = this.productDetails?.querySelector('.group-block-content');
    this.railContent = this.querySelector('[data-mobile-purchase-rail-content]');

    if (!this.productDetails || !this.sourceContainer || !this.railContent) return;

    if (!this.entries) {
      this.entries = this.buildEntries();
    }

    if (!this.entries.length) return;

    this.bindMediaQuery();
    this.syncLayout();
  }

  disconnectedCallback() {
    this.unbindMediaQuery();
    this.disconnectResizeObserver();
    this.restore();
  }

  bindMediaQuery() {
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.syncLayout);
      return;
    }

    this.mediaQuery.addListener(this.syncLayout);
  }

  unbindMediaQuery() {
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.syncLayout);
      return;
    }

    this.mediaQuery.removeListener(this.syncLayout);
  }

  syncLayout = () => {
    if (!this.entries?.length) return;

    if (this.mediaQuery.matches) {
      this.dock();
    } else {
      this.restore();
    }
  };

  buildEntries() {
    const children = Array.from(this.sourceContainer.children).filter((child) => child instanceof HTMLElement);

    const titleBlock = children.find((child) => this.isTitleBlock(child));
    const fallbackTitleBlock = children.find((child) => child.classList.contains('view-product-title'));

    const titleNode = titleBlock || fallbackTitleBlock || null;
    const priceNode = children.find((child) => this.isPriceBlock(child));
    const variantNode = children.find((child) => this.isVariantBlock(child));
    const buyButtonsNode = children.find((child) => this.isBuyButtonsBlock(child));

    const selectedNodes = [titleNode, priceNode, variantNode, buyButtonsNode].filter(Boolean);

    if (!selectedNodes.length) return [];

    const indexes = selectedNodes.map((node) => children.indexOf(node));
    const startIndex = Math.min(...indexes);
    const endIndex = Math.max(...indexes);

    return children
      .slice(startIndex, endIndex + 1)
      .filter((child) => selectedNodes.includes(child) || this.isDividerBlock(child))
      .map((node) => {
        const placeholder = document.createElement('span');
        placeholder.hidden = true;
        placeholder.dataset.mobilePurchaseRailPlaceholder = '';
        node.before(placeholder);

        return { node, placeholder };
      });
  }

  dock() {
    if (!this.entries?.length || !this.railContent) return;

    this.removeAttribute('hidden');
    this.removeAttribute('aria-hidden');

    for (const entry of this.entries) {
      this.railContent.appendChild(entry.node);
    }

    // Keep the final footer link scrollable above the fixed purchase controls.
    if (!this.footerClearance) {
      this.footerClearance = document.createElement('div');
      this.footerClearance.className = 'mobile-purchase-rail-clearance';
      this.footerClearance.setAttribute('aria-hidden', 'true');
    }
    document.body.appendChild(this.footerClearance);

    this.productDetails?.setAttribute('data-mobile-purchase-rail-active', 'true');
    this.observeRail();
    this.updateHeight();
  }

  restore() {
    if (!this.entries?.length) return;

    for (const entry of this.entries) {
      entry.placeholder.after(entry.node);
    }

    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.productDetails?.removeAttribute('data-mobile-purchase-rail-active');
    this.productDetails?.style.removeProperty('--mobile-purchase-rail-height');
    this.disconnectResizeObserver();
    this.footerClearance?.remove();
  }

  observeRail() {
    if (!('ResizeObserver' in window)) return;

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
    }

    this.resizeObserver.observe(this);
  }

  disconnectResizeObserver() {
    this.resizeObserver?.disconnect();
  }

  updateHeight() {
    if (this.hasAttribute('hidden') || !this.productDetails) return;

    const height = Math.ceil(this.getBoundingClientRect().height);
    this.productDetails.style.setProperty('--mobile-purchase-rail-height', `${height}px`);
    this.footerClearance?.style.setProperty('--mobile-purchase-rail-height', `${height}px`);
  }

  isTitleBlock(node) {
    return node.classList.contains('text-block') && !!node.querySelector('h1');
  }

  isPriceBlock(node) {
    return node.tagName === 'PRODUCT-PRICE';
  }

  isVariantBlock(node) {
    return node.tagName === 'VARIANT-PICKER';
  }

  isBuyButtonsBlock(node) {
    return node.classList.contains('buy-buttons-block');
  }

  isDividerBlock(node) {
    return node.classList.contains('divider');
  }
}

if (!customElements.get('product-mobile-purchase-rail')) {
  customElements.define('product-mobile-purchase-rail', ProductMobilePurchaseRail);
}
